import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications"; // UPDATED IMPORTS
import { useDebounce } from "../hook/useDebounce";

const LocationsTopListPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  
  const debouncedSearch = useDebounce(search, 500);

  // Wrapped in useCallback for consistency across your location pages
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/top-locations", {
        params: { 
          search: debouncedSearch, 
          page, 
          limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction 
        },
      });
      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch {
      showToast("error", "Failed to load top locations"); // UPDATED
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DataTable
      title="Top Locations"
      data={data}
      loading={loading}
      searchTerm={search}
      onSearchChange={(v) => {
        setSearch(v);
        setPage(1);
      }}
      page={page}
      limit={limit}
      total={total}
      onPageChange={setPage}
      showCheckbox={false} 
      onLimitChange={(v) => {
        setLimit(v);
        setPage(1);
      }}
      
      sortConfig={sortConfig}
      onSortChange={(config) => {
        setSortConfig(config);
        setPage(1); 
      }}

      headerActions={[
        {
          label: "Add Location",
          icon: "ri-add-line",
          className: "btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2",
          onClick: () => navigate("/locations-top/new"),
        },
      ]}

      columns={[
        { 
          key: "name", 
          label: "Location Name", 
          className: "text-md fw-semibold text-primary-light",
          sortable: true 
        },
        { 
          key: "description", 
          label: "Description", 
          className: "text-secondary-light fw-medium",
          sortable: true 
        },
      ]}

      actions={[
        {
          icon: "iconamoon:eye-light",
          className: "w-32-px h-32-px bg-primary-light text-primary-600 rounded d-flex align-items-center justify-content-center border-0",
          onClick: (row) =>
            navigate("/locations-main", {
              state: { filterAreaId: row.id, filterAreaName: row.name },
            }),
        },
        {
          icon: "lucide:edit",
          className: "w-32-px h-32-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0",
          onClick: (row) => navigate(`/locations-top/${row.id}/edit`),
        },
        {
          icon: "mingcute:delete-2-line",
          className: "w-32-px h-32-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0",
          onClick: (row) => {
            // REPLACED window.confirm with confirmDelete helper
            confirmDelete(row.name, async () => {
              try {
                await api.delete(`/top-locations/${row.id}`);
                showToast("success", "Top location deleted"); // UPDATED
                fetchData();
              } catch {
                showToast("error", "Delete operation failed"); // UPDATED
              }
            });
          },
        },
      ]}
    />
  );
};

export default LocationsTopListPage;